// src/utils/licenseVerificationPaymentUtils.js

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASEURL;

// Get License Verification by ID with payment info
export const getLicenseVerificationById = async (licenseId) => {
  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    const response = await axios.get(
      `${BASE_URL}/getLicenseVerificationById/${licenseId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Get license verification by ID error:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch license verification details');
  }
};

// Load Razorpay script
export const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(script);
  });
};

// Create License Verification Order
export const createLicenseVerificationOrder = async (licenseId, amountInRupees) => {
  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    const response = await axios.post(
      `${BASE_URL}/createLicenseVerificationOrder/${licenseId}`,
      {
        amountInRupees: amountInRupees,
        amountInPaise: amountInRupees * 100,
        currency: 'INR'
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Create license verification order error:', error);
    throw new Error(error.response?.data?.message || 'Failed to create order');
  }
};

// Verify License Verification Payment
export const verifyLicenseVerificationPayment = async (licenseId, paymentResponse) => {
  try {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    const response = await axios.post(
      `${BASE_URL}/verifyLicenseVerificationPayment/${licenseId}`,
      {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        amount_paise: paymentResponse.amount_paise,
        amount_rupees: paymentResponse.amount_rupees
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Verify license verification payment error:', error);
    throw new Error(error.response?.data?.message || 'Payment verification failed');
  }
};

// Open Razorpay Checkout for License Verification
export const openLicenseVerificationCheckout = async ({
  orderData,
  licenseData,
  onSuccess,
  onFailure
}) => {
  try {
    await loadRazorpayScript();
    
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.data.amount,
      currency: orderData.data.currency,
      name: 'AIOTA License Verification',
      description: 'License Verification Payment',
      order_id: orderData.data.order_id,
      prefill: {
        name: licenseData.authorityName || '',
        email: licenseData.email,
        contact: licenseData.contact
      },
      notes: {
        module: 'licenseverification',
        license_id: licenseData._id,
        authority_name: licenseData.authorityName,
        aiota_membership_number: licenseData.aiotaMembershipNumber
      },
      handler: function(response) {
        console.log('License verification payment success:', response);
        if (onSuccess) onSuccess(response);
      },
      modal: {
        ondismiss: function() {
          console.log('License verification checkout modal closed');
          if (onFailure) onFailure(new Error('Payment cancelled by user'));
        }
      },
      theme: {
        color: '#528FF0'
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error('Error opening license verification checkout:', error);
    if (onFailure) onFailure(error);
  }
};

// Get error message
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Validate payment response
export const validatePaymentResponse = (response) => {
  return response && 
         response.razorpay_order_id && 
         response.razorpay_payment_id && 
         response.razorpay_signature;
};

export default {
  getLicenseVerificationById,
  loadRazorpayScript,
  createLicenseVerificationOrder,
  verifyLicenseVerificationPayment,
  openLicenseVerificationCheckout,
  getErrorMessage,
  validatePaymentResponse
};
